import React, {useCallback, useEffect, useState} from "react";
import styles from './Carousel.module.css'
import {
  ChoosePlansMenuContainer, ChoosePlansNavigationIcon, ChoosePlansSelectionContainer, ChoosePlansDropdownContainer,
  ChoosePlansDropdownText, ChoosePlansDropdownIcon, ChoosePlansDropdownList, ChoosePlansDropdownItem, ChoosePlansCards,
  ChoosePlansCardsHeadersContainer, ChoosePlansCardsHeader, ChoosePlansCardsSubText, ChooesPlansCardsCarousel
} from './Carousel.styles'

interface CarouselProps {
  style?: React.CSSProperties,
  children: React.ReactChild[] | React.ReactChild,
  containerClassName?: string,
  cardClassName?: string,
  rotationInterval?: number,
  autoRotate?: boolean,
  planType: string,
  setPlanType: (plan: string) => void
}

export interface CarouselIndexes {
  previousIndex: number,
  currentIndex: number,
  nextIndex: number
}

// credits to repo: https://github.com/saadqbal/react-stacked-carousel
function Carousel(props: CarouselProps) {
  const {
    style, containerClassName, cardClassName, autoRotate = false, rotationInterval = 2000,
    planType, setPlanType
  } = props;
  let children = React.Children.toArray(props.children);

  useEffect(() => {
    setIndexes({
      previousIndex: children.length - 1,
      currentIndex: 0,
      nextIndex: 1
    })
  }, [planType])

  const [dropdownActive, setDropdownActive] = useState(false);
  const [indexes, setIndexes] = useState<CarouselIndexes>({
    previousIndex: children.length - 1,
    currentIndex: 0,
    nextIndex: 1
  });

  const setCardStyle = (indexes: CarouselIndexes, cardIndex: number) => {
    if (indexes.currentIndex === cardIndex) return styles.active;
    if (indexes.nextIndex === cardIndex) return styles.next;
    if (indexes.previousIndex === cardIndex) return styles.prev;

    return styles.inactive;
  }

  const handleLeftIndexes = useCallback(() => {
    // If we've reached the end, start again from the first card,
    // but carry previous value over
    if (indexes.currentIndex <= 0) {
      setIndexes({
        previousIndex: children.length - 2,
        currentIndex: children.length - 1,
        nextIndex: 0
      });

    } else {
      setIndexes(prevState => ({
        nextIndex: prevState.currentIndex,
        currentIndex: prevState.currentIndex - 1,
        previousIndex:
          prevState.currentIndex - 1 <= 0
            ? children.length - 1
            : prevState.currentIndex - 2
      }));
    }
  }, [children.length, indexes.currentIndex]);

  const handleRightIndexes = useCallback(() => {
    if (indexes.currentIndex >= children.length - 1) {
      setIndexes({
        previousIndex: children.length - 1,
        currentIndex: 0,
        nextIndex: 1
      });
    } else {
      setIndexes(prevState => ({
        previousIndex: prevState.currentIndex,
        currentIndex: prevState.currentIndex + 1,
        nextIndex:
          prevState.currentIndex + 2 === children.length
            ? 0
            : prevState.currentIndex + 2
      }));
    }
  }, [children.length, indexes.currentIndex]);

  useEffect(() => {
    const transitionInterval = setInterval(() => {
      autoRotate && handleRightIndexes();
    }, rotationInterval);
    return () => clearInterval(transitionInterval);
  }, [handleRightIndexes, indexes, autoRotate, rotationInterval]);

  let childrenWithProps = React.Children.map(children, (child, index) => {
    // checking isValidElement is the safe way and avoids a typescript error too.
    if (React.isValidElement(child)) {
      return (
        <li className={`${styles.card} ${setCardStyle(indexes, index)}`}>
          {React.cloneElement(child as React.ReactElement<any>, {

            handleLeftIndexes, handleRightIndexes, index, indexes
          })}
        </li>
      );
    }
    return child;
  });

  return (
    <ChoosePlansMenuContainer>
      {
        children.length > 1 ?
          <ChoosePlansNavigationIcon className={'fa-solid fa-angle-left'} onClick={handleLeftIndexes}/>
          : null
      }
      <ChoosePlansSelectionContainer>
        <ChoosePlansDropdownContainer onClick={() => setDropdownActive(!dropdownActive)}>
          <ChoosePlansDropdownText>{planType}</ChoosePlansDropdownText>
          <ChoosePlansDropdownIcon className={`fa-solid fa-angle-down`} dropdownActive={dropdownActive}/>

          <ChoosePlansDropdownList dropdownActive={dropdownActive}>
            <ChoosePlansDropdownItem onClick={() => setPlanType('Business')}> Business </ChoosePlansDropdownItem>
            <ChoosePlansDropdownItem onClick={() => setPlanType('Non-Profits')}> Non-Profits </ChoosePlansDropdownItem>
            {/* <ChoosePlansDropdownItem onClick={() => setPlanType('School')}> School </ChoosePlansDropdownItem>
            <ChoosePlansDropdownItem onClick={() => setPlanType('Family')}> Family </ChoosePlansDropdownItem> */}
          </ChoosePlansDropdownList>
        </ChoosePlansDropdownContainer>

        <ChoosePlansCards>
          <ChoosePlansCardsHeadersContainer>
            <ChoosePlansCardsHeader>Choose A Plan That Works Best For Your Organization</ChoosePlansCardsHeader>
            <ChoosePlansCardsSubText>
              Celebrate Your Entire Team By Upgrading To A Premium Subsciption Today
            </ChoosePlansCardsSubText>
          </ChoosePlansCardsHeadersContainer>

          <ChooesPlansCardsCarousel>
            <div className={`${styles.container}`}>
              <ul style={{...style}} className={`${styles.cardCarousel}`}>
                {
                  childrenWithProps
                }
              </ul>
            </div>

          </ChooesPlansCardsCarousel>
        </ChoosePlansCards>
      </ChoosePlansSelectionContainer>

      {
        children.length > 1 ?
          <ChoosePlansNavigationIcon className={'fa-solid fa-angle-right'} onClick={handleRightIndexes}/>
          : null
      }
    </ChoosePlansMenuContainer>
  );
}

export default Carousel;
